import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { AppDialogSection, AppDialog, AppDialogContent } from '@/UI/AppDialog/AppDialog';
import { Fade } from '@/UI/Transitions/Fade';
import { H2, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { Tag } from '@/UI/Tag/Tag';
import { Box } from '@/UI/Box/Box';
import { Flex, FlexCenHorVer, FlexCol } from '@/UI/styles';
import { color } from '@/colors';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_MEET_WEAVE_MODAL_MEDIA } from '@/consts/featureFlags';
import { I18N_KEYS } from '@/language/keys';
import { resolveMeetWeaveMedia } from './meetWeaveMedia';
import artworkPoster from './assets/meet-weave-artwork-poster.png';
import artworkVideo from './assets/meet-weave-artwork.mp4';

// Mirrors StartOfTourModal's two-column AppDialog layout (text column + artwork), but with a single
// "Get started" CTA. Design: Weave-Onboarding node 54877-27725.
const CONTENT_WIDTH = 368;
const ARTWORK_WIDTH = 332;
const HEIGHT = 404;

interface MeetWeaveModalProps {
  open: boolean;
  title: string;
  body: string;
  disallowClose?: boolean;
  onClose: () => void;
  // advances/finishes the tour; for the single-step welcome tour this completes it.
  onNextStep: () => void;
}

export function MeetWeaveModal({ open, title, body, disallowClose = false, onClose, onNextStep }: MeetWeaveModalProps) {
  const { t } = useTranslation();
  const isFigmaEmbedMode = useIsFigmaEmbedMode();
  // Artwork URLs come from the `meet-weave-modal-media` flag payload when set (so the clip can be swapped
  // from the PostHog dashboard without a release), otherwise the bundled asset.
  const { variantData: mediaOverride } = useFeatureFlag(FF_MEET_WEAVE_MODAL_MEDIA);
  const { videoUrl, posterUrl } = resolveMeetWeaveMedia(mediaOverride, {
    videoUrl: artworkVideo,
    posterUrl: artworkPoster,
  });

  return (
    <AppDialog
      open={open}
      onClose={disallowClose ? undefined : onClose}
      slots={{ transition: Fade }}
      slotProps={{
        paper: {
          sx: {
            background: token.bg,
            height: HEIGHT,
            maxWidth: CONTENT_WIDTH + ARTWORK_WIDTH,
            width: '100%',
            borderRadius: '13px',
          },
        },
        transition: { timeout: 0 },
        backdrop: { slotProps: { transition: { timeout: 0 } } },
      }}
    >
      <AppDialogSection>
        <AppDialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <FlexCenHorVer sx={{ maxHeight: '100%' }}>
            <FlexCol sx={{ width: CONTENT_WIDTH, height: HEIGHT, justifyContent: 'space-between', p: 4 }}>
              <FlexCol sx={{ flex: 1, justifyContent: 'center', gap: 3 }}>
                <FlexCol sx={{ gap: 1, alignItems: 'flex-start' }}>
                  {isFigmaEmbedMode && (
                    <Tag
                      text={t(I18N_KEYS.FIGMA_EMBED.BETA_BADGE)}
                      bgColor={color.Black84}
                      textColor={color.White64_T}
                      disableTextTransform
                      sx={{
                        height: '16px',
                        borderRadius: '5px',
                        // UI3 body.medium typography (Tag defaults to a monospace label
                        // variant; override so the Beta badge matches Figma's UI3 type ramp).
                        fontFamily: '"DM Sans", system-ui, -apple-system, Arial, sans-serif',
                        fontSize: '11px',
                        fontWeight: 450,
                        lineHeight: '16px',
                        letterSpacing: '0.055px',
                      }}
                    />
                  )}
                  <H2 sx={{ color: token.text }}>{title}</H2>
                  <TextStdRg sx={{ color: token.text, lineHeight: 'initial' }}>{body}</TextStdRg>
                </FlexCol>
                {/* wrap in a row so the button keeps its natural (compact) width instead of stretching */}
                <Flex>
                  <ButtonContained size="small" onClick={onNextStep}>
                    {t(I18N_KEYS.PRODUCT_TOURS.WELCOME_TEMPLATE_GALLERY.GET_STARTED)}
                  </ButtonContained>
                </Flex>
              </FlexCol>
            </FlexCol>
            <Box sx={{ width: ARTWORK_WIDTH, height: HEIGHT, bgcolor: color.Black84, flexShrink: 0 }}>
              {/* poster (the clip's first frame) shows instantly while the clip loads, then it autoplays muted on loop */}
              <video
                src={videoUrl}
                poster={posterUrl}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
          </FlexCenHorVer>
        </AppDialogContent>
      </AppDialogSection>
    </AppDialog>
  );
}
